<script lang="ts" setup>
import {
  computed,
  onMounted,
  onUnmounted,
  ref,
  useTemplateRef,
  watch,
} from 'vue';
import { useRoute } from 'vue-router';
import api from '@/api';
import { useSnackbar } from '@/components/common/snackbar/use-snackbar';
import FavoriteThemesSection from '@/components/home/favorite-themes-section.vue';
import FeaturedArticlesNext from '@/components/home/featured-articles-next.vue';
import IndustryNewsNext from '@/components/home/industry-news-next.vue';
import InsightsNext from '@/components/home/insights/insights-next.vue';
import MemberActivity from '@/components/home/member-activity.vue';
import MobileAppInfo from '@/components/home/mobile-app-info.vue';
import OtherAgents from '@/components/home/other-agents.vue';
import PersonalNewsArticles from '@/components/home/personal-news-articles.vue';
import PersonalNewsCountdown from '@/components/home/personal-news-countdown-next.vue';
import PersonalNewsOnboardingPrompt from '@/components/home/personal-news-onboarding-prompt.vue';
import { AdpDocument, Comment } from '@/types';
import { useLayoutScrollContainer } from '@/utils/composables/useLayoutScrollContainer';
import { usePersonalNewsFeedPolling } from '@/utils/composables/usePersonalNewsFeedPolling';
import { useWebPush } from '@/utils/composables/useWebPush';
import { STATES, useThemeList, useUserInfo } from '@/utils/swr';
import { useEmitter, useStore } from '@/utils/vue';

const store = useStore();
const route = useRoute();
const emitter = useEmitter();
const { createSnackbar } = useSnackbar();
const { scrollToTop } = useLayoutScrollContainer();
const layoutVariant = 'next';

const feedType = computed(() => store.state.feedType.feedType);
const { data: userInfo, state: userState, mutate } = useUserInfo();
const { state: themesState } = useThemeList(feedType);
const { requestWebPushPermissionOnHomeVisit } = useWebPush();

const actionedArticleCounts = computed(
  () => userInfo.value?.personal_news_counts ?? { domestic: 0, foreign: 0 },
);
const requiredActionCount = 10;
const isFeedIncludingForeign = computed(() =>
  ['all', 'foreign'].includes(feedType.value),
);
const actionCount = computed(() => {
  return isFeedIncludingForeign.value
    ? actionedArticleCounts.value.foreign
    : actionedArticleCounts.value.domestic;
});
const isPreparingFeed = computed(
  () => actionCount.value >= requiredActionCount,
);

const { hasPersonalNews } = usePersonalNewsFeedPolling(isPreparingFeed);

// mutate() 後の userState の一時的な変化で isLoaded が false に戻り、
// ページ全体が再レンダーされるのを防ぐため、最初に成功した時点のみ true にする。
const hasLoadedUser = ref(false);
watch(
  userState,
  newUserState => {
    if (newUserState === STATES.SUCCESS) {
      hasLoadedUser.value = true;
    }
  },
  { immediate: true },
);

const isLoaded = computed(
  () => hasLoadedUser.value && themesState.value === STATES.SUCCESS,
);

const hasMyComment = (comments: Comment[], userId: number): boolean =>
  comments.some(
    c => c.user_id === userId || hasMyComment(c.children ?? [], userId),
  );

const findMyActionLabel = (doc: AdpDocument) => {
  const userId = userInfo.value?.id;
  if (userId === undefined) return;
  if (doc.marks?.some(m => m.user_id === userId)) return 'マーク';
  if (doc.group_marks?.some(m => m.creator_id === userId))
    return 'グループマーク';
  if (hasMyComment(doc.comments ?? [], userId)) return 'コメント';
};

const handleArticleUpdate = async (doc: AdpDocument) => {
  if (hasPersonalNews.value || isPreparingFeed.value) return;
  const previousCount = actionCount.value;
  await mutate();
  if (actionCount.value <= previousCount) return;

  const label = findMyActionLabel(doc);
  if (!label) return;

  createSnackbar({
    type: 'progress',
    message: `「${doc.title}」に${label}。この分野を優先して集めます。`,
    params: {
      current: actionCount.value,
      total: requiredActionCount,
    },
  });

  if (isPreparingFeed.value) {
    scrollToTop('smooth');
  }
};

const handleArticleView = () => {
  if (hasPersonalNews.value || isPreparingFeed.value) return;
  mutate();
};

onMounted(async () => {
  api.trackPageView({
    pageName: 'home',
    pageUrl: route.fullPath,
    layoutVariant,
  });
  await requestWebPushPermissionOnHomeVisit(createSnackbar);
  emitter.on('article-updated', handleArticleUpdate);
  emitter.on('article-viewed', handleArticleView);
});
onUnmounted(() => {
  emitter.off('article-updated', handleArticleUpdate);
  emitter.off('article-viewed', handleArticleView);
});

const MARK_HINT_DURATION_MS = 4200;
// 既に最上部にいると scrollend が発火しない。また未対応ブラウザもあるため、フォールバックで開始する。
const SCROLL_END_FALLBACK_MS = 1000;

const isMarkHintActive = ref(false);
let markHintTimer: ReturnType<typeof setTimeout> | undefined;
let scrollEndFallbackTimer: ReturnType<typeof setTimeout> | undefined;

const stopMarkHint = () => {
  clearTimeout(markHintTimer);
  clearTimeout(scrollEndFallbackTimer);
  window.removeEventListener('scrollend', startMarkHint, { capture: true });
  isMarkHintActive.value = false;
};

function startMarkHint() {
  stopMarkHint();
  isMarkHintActive.value = true;
  markHintTimer = setTimeout(stopMarkHint, MARK_HINT_DURATION_MS);
}

onUnmounted(stopMarkHint);

const pageRef = useTemplateRef('aconnect-home');
const scrollToIndustryMemberRow = () => {
  stopMarkHint();
  pageRef.value?.scrollIntoView({
    block: 'start',
    behavior: 'smooth',
  });
  window.addEventListener('scrollend', startMarkHint, {
    capture: true,
    once: true,
  });
  scrollEndFallbackTimer = setTimeout(startMarkHint, SCROLL_END_FALLBACK_MS);
};
</script>

<template>
  <div v-if="isLoaded" ref="aconnect-home" class="aconnect-home">
    <PersonalNewsCountdown
      v-if="!hasPersonalNews && !isPreparingFeed"
      :action-count="actionCount"
      :required-count="requiredActionCount"
      class="personal-news-countdown"
    />
    <InsightsNext />

    <template v-if="hasPersonalNews || isPreparingFeed">
      <PersonalNewsOnboardingPrompt
        v-if="isPreparingFeed && !hasPersonalNews"
        :action-count="actionCount"
        :required-count="requiredActionCount"
        :is-preparing-feed="isPreparingFeed"
        :sent-first-personal-news-feed-at="
          userInfo?.sent_first_personal_news_feed_at ?? undefined
        "
      />
      <PersonalNewsArticles v-else />

      <div class="content-row">
        <div class="main-column">
          <div class="industry-member-row">
            <IndustryNewsNext class="panel" />
            <MemberActivity class="panel" />
          </div>
          <FavoriteThemesSection class="favorite-themes" />
        </div>
        <div class="side-column">
          <FeaturedArticlesNext class="panel" />
          <OtherAgents />
          <MobileAppInfo />
        </div>
      </div>
    </template>

    <template v-else>
      <div class="content-row">
        <div
          class="industry-member-row"
          :class="{ 'mark-hint': isMarkHintActive }"
        >
          <IndustryNewsNext class="panel" />
          <MemberActivity class="panel" />
        </div>
        <FeaturedArticlesNext
          class="panel"
          :class="{ 'mark-hint': isMarkHintActive }"
        />
      </div>

      <PersonalNewsOnboardingPrompt
        :action-count="actionCount"
        :required-count="requiredActionCount"
        :is-preparing-feed="isPreparingFeed"
        @start-clicked="scrollToIndustryMemberRow()"
      />

      <div class="content-row">
        <FavoriteThemesSection class="favorite-themes" />
        <div class="side-column">
          <OtherAgents />
          <MobileAppInfo />
        </div>
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.aconnect-home {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px 24px;
  max-width: 1296px;
  margin: 0 auto;

  .content-row {
    display: flex;
    gap: 16px;
    min-width: 0;

    // gap: 16px があるので３つのカラムを同じ幅になるように計算が必要
    > .main-column,
    > .industry-member-row {
      flex: 0 1 calc((100% - 32px) / 3 * 2 + 16px);
    }
    > .favorite-themes {
      flex: 2;
    }
    > .side-column,
    > .panel {
      flex: 0 1 calc((100% - 32px) / 3);
      align-self: flex-start;
    }
    > .industry-member-row {
      align-self: flex-start;
    }
  }

  .main-column {
    display: flex;
    flex-direction: column;
    gap: 16px;
    min-width: 0;

    > .favorite-themes {
      flex: 1;
    }
  }

  .industry-member-row {
    display: flex;
    gap: 16px;
    min-width: 0;
  }

  .panel {
    flex: 1;
    min-width: 0;
  }

  .favorite-themes {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  .side-column {
    display: flex;
    flex-direction: column;
    gap: 16px;
    min-width: 0;
  }

  .personal-news-countdown {
    position: sticky;
    top: calc(var(--layout-sticky-top, 0) + 8px);
    z-index: 100;
  }

  .mark-hint
    :deep(
      .mark-button-field:not(.mark-button-active):not(.disabled) .mark-button
    ) {
    position: relative;
  }
  .mark-hint
    :deep(
      .mark-button-field:not(.mark-button-active):not(.disabled)
        .mark-button::after
    ) {
    position: absolute;
    content: '';
    left: 4px;
    top: 4px;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: $color-green600;
    pointer-events: none;
    animation: mark-glow 1.6s cubic-bezier(0.22, 0.61, 0.36, 1) infinite;
  }
}

@keyframes mark-glow {
  0% {
    transform: scale(0);
    opacity: 0.4;
  }
  60% {
    opacity: 0.14;
  }
  100% {
    transform: scale(5.6);
    opacity: 0;
  }
}
</style>
